import React from 'react';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroller';

import {
    INVESTIGATION_ENTITY_TYPES,
    POLICY_ACTION_TYPES,
    USER_TYPES,
    RELYING_PARTY_SERVER_NAME,
    AD_ENTITY_TYPES,
    AUTHENTICATION_LOG_REASONS,
    DIRECTORY_TYPES,
    DENY_REASONS,
    RISK_LEVELS,
    RISK_CHANGE_TYPE,
    INCIDENT_STATUS,
    ACTIVITY_TYPES,
    PRIVILEGED_ACCOUNTS_STATUS,
    INCIDENT_TYPE
} from '@silverfort-nodelibs/common/utils/consts';
import { getRiskIndicatorByRiskRule, getIconPathByRiskIndicator } from '@silverfort-nodelibs/common/utils/insight';

import allowEventBlueIcon from '../../Images/allowEventBlue.png';
import mfaEventIcon from '../../Images/mfaEventWhite.png';
import denyEventIcon from '../../Images/denyEventWhite.png';
import alertIcon from '../../Images/alert_white.png';
import userIcon from '../../../common/Images/user.png';
import resourceIcon from '../../../common/Images/server.png';
import applicationIcon from '../../../common/Images/cloud_app_blue.svg';
import collapseIcon from '../../../common/Images/dropdown_expanded.png';
import expandIcon from '../../../common/Images/dropdown_collapsed.png';

import styles from './InvestigationTimeline.module.css';
import investigationStyles from '../../Investigation.module.css';
import { formatUserResponse, formatDestination, getEnumLabelByValue } from '@silverfort-nodelibs/common/utils/common';
import LogRowExpender from '../../../Logs/LogRowExpender';
import { getIconFromIconPath } from '../../../Insights/insights-utils';

class TimelineRow extends React.Component {
    constructor() {
        super();
        this.state = { data: {}, isExpened: false };
        const locale = navigator.languages ? navigator.languages : navigator.language;
        moment.locale(locale);
    }

    componentDidMount() {
        const data = this.props.data;
        if (data.activityType === ACTIVITY_TYPES.RISK_CHANGE) {
            if (this.props.data.risk_indicator_type) {
                data.riskChangeType = RISK_CHANGE_TYPE.INDICATOR_CHANGE;
            } else if (this.props.data.old_risk_level) {
                data.riskChangeType = RISK_CHANGE_TYPE.LEVEL_CHANGE;
            }
        } else if (data.activityType === ACTIVITY_TYPES.AUTH_LOG) {
            if (!this.props.data.effective_rule_action) {
                data.policyType = this.props.data.action
                    ? POLICY_ACTION_TYPES.ALLOW.value
                    : POLICY_ACTION_TYPES.DENY.value;
            } else {
                data.policyType = POLICY_ACTION_TYPES[this.props.data.effective_rule_action.toUpperCase()].value;
            }
        }

        data.location =
            this.props.geoLocationEnabled &&
            (('geo_device_longitude' in data &&
                data.geo_device_longitude &&
                data.geo_device_longitude.toString() !== 0) ||
                ('geo_device_latitude' in data &&
                    data.geo_device_latitude &&
                    data.geo_device_latitude.toString() !== 0));
        data.isLoaded = true;
        this.setState({ data: data });
    }

    getRelyingPartyName(serverGuid, server, service) {
        let relyingPartyName = server;
        if (this.props.relyingParties && this.props.relyingParties[serverGuid]) {
            relyingPartyName = this.props.relyingParties[serverGuid].name;
        } else if (service) {
            relyingPartyName = service;
        }
        return relyingPartyName;
    }

    render() {
        if (!this.state.data || !typeof this.state.data === 'object' || Object.keys(this.state.data).length === 0) {
            return <></>;
        }

        let headline = null;

        let hostNameSection = null,
            usernameSection = null,
            serverSection = null;

        let isDevice = false,
            isHost = false;

        let riskIndicatorIcon = null;
        if (this.state.data.riskChangeType === RISK_CHANGE_TYPE.INDICATOR_CHANGE) {
            let riskDescription = '';
            const riskIndicator = getRiskIndicatorByRiskRule(this.state.data.risk_indicator_type);
            riskIndicatorIcon = getIconFromIconPath(getIconPathByRiskIndicator(riskIndicator, true));

            if (riskIndicator) {
                riskDescription = riskIndicator.entityTitle;
            } else if (this.state.data.description) {
                riskDescription = this.state.data.description;
            } else {
                riskDescription = this.state.data.risk_indicator_type;
            }
            riskDescription = riskDescription.toUpperCase();
            headline = (
                <h2>{`${riskDescription} - Risk indicator was ${
                    this.state.data.is_deleted ? 'eliminated' : 'detected'
                }`}</h2>
            );
        } else if (this.state.data.riskChangeType === RISK_CHANGE_TYPE.LEVEL_CHANGE) {
            const riskLevel = Object.values(RISK_LEVELS)
                .find((risk) => risk.value === this.state.data.risk_level)
                .label.toUpperCase();
            const oldRiskLevel = Object.values(RISK_LEVELS)
                .find((risk) => risk.value === this.state.data.old_risk_level)
                .label.toUpperCase();
            headline = <h2>{`Entity risk level was changed from ${oldRiskLevel} to ${riskLevel}`}</h2>;
        } else if (this.state.data.activityType === ACTIVITY_TYPES.JIT_ACTION) {
            headline = (
                <h2>
                    {this.state.data.action === PRIVILEGED_ACCOUNTS_STATUS.Enabled ? 'Enabled ' : 'Disable '}
                    {this.state.data.reason ? `with reason ${this.state.data.reason}` : ''} (JIT policy)
                </h2>
            );
        } else if (this.state.data.activityType === ACTIVITY_TYPES.INCIDENT_CHANGE) {
            const oldStatus = getEnumLabelByValue(INCIDENT_STATUS, this.state.data.old_status);
            const status = getEnumLabelByValue(INCIDENT_STATUS, this.state.data.status);

            const changedByGuid = this.state.data.changed_by_guid;
            const changedByDisplayName = this.state.data.changed_by_display_name;

            const onChangedByClicked = changedByGuid
                ? () => this.props.handleInvestigationClick(changedByGuid, changedByDisplayName, AD_ENTITY_TYPES.USER)
                : () => {};

            headline = (
                <h2>
                    {`Incident status was changed from `}
                    <span className={`${styles.bold} ${styles.flex}`}>
                        {oldStatus}
                        <span className={styles.nonBold}>to</span>
                        {status}
                        <span className={styles.nonBold}>by</span>
                        <span
                            className={`${styles.entity} ${!changedByGuid ? styles.disabled : ''}`}
                            onClick={onChangedByClicked}>
                            {changedByDisplayName && changedByDisplayName !== 'Silverfort' && (
                                <img src={userIcon} alt="" />
                            )}
                            {changedByDisplayName || 'n/a'}
                        </span>
                    </span>
                </h2>
            );
        } else if (this.state.data.activityType === ACTIVITY_TYPES.EVENTS_LOG) {
            const incidentType = Object.values(INCIDENT_TYPE).find((t) => t.label === this.state.data.incident?.kind);
            if (incidentType?.logTemplate) {
                headline = <h2>{incidentType.logTemplate(this.state.data)}</h2>;
            }
        } else {
            if (this.state.data.host_name && this.state.data.host_name.toLowerCase() !== 'n/a') {
                isHost = this.state.data.user_type !== USER_TYPES.MACHINE_ACCOUNT;

                if (isHost) {
                    hostNameSection = this.state.data.host_guid ? (
                        <span
                            className={styles.entity}
                            onClick={() =>
                                this.props.handleInvestigationClick(
                                    this.state.data.host_guid,
                                    this.state.data.host_name,
                                    AD_ENTITY_TYPES.SERVER
                                )
                            }>
                            <img src={resourceIcon} alt="" />
                            {this.state.data.host_name}
                        </span>
                    ) : (
                        this.state.data.host_name
                    );
                }
                if (this.state.data.host_name.toLowerCase() === this.props.entityID) {
                    isDevice = true;
                }
            }

            if (this.state.data.user_guid || this.state.data.user_identifier) {
                const isMachineAccount = this.state.data.user_type === USER_TYPES.MACHINE_ACCOUNT;
                const userName = this.state.data.user_guid
                    ? this.state.data.user_guid
                    : this.state.data.user_identifier;
                const isNa = userName.toLowerCase() === 'n/a';
                if (!isNa) {
                    const disable = isMachineAccount;
                    const onUserNameClicked = disable
                        ? () => {}
                        : () =>
                              this.props.handleInvestigationClick(
                                  this.state.data.user_guid
                                      ? this.state.data.user_guid
                                      : encodeURI(this.state.data.user_identifier),
                                  this.state.data.upn ? this.state.data.upn : this.state.data.user_identifier,
                                  AD_ENTITY_TYPES.USER
                              );

                    usernameSection = (
                        <span
                            className={`${styles.entity} ${disable ? styles.disabled : ''}`}
                            onClick={onUserNameClicked}>
                            <img src={userIcon} alt="" />
                            {this.state.data.upn ? this.state.data.upn : this.state.data.user_identifier}
                        </span>
                    );
                }
            }

            const [logDestination, isDefaultDestination] = formatDestination(
                this.state.data.server,
                this.state.data.server_ip,
                this.state.data.protocol_type,
                this.state.data.directory_type,
                this.props.defaultDestinations
            );

            if (logDestination) {
                const { server_guid, server, service, is_application, directory_type } = this.state.data;

                if (server.toLowerCase() === RELYING_PARTY_SERVER_NAME) {
                    const relyingPartyName = this.getRelyingPartyName(server_guid, server, service);
                    serverSection = <span className={styles.bold}>{relyingPartyName} &nbsp;</span>;
                } else if (isDefaultDestination) {
                    serverSection = <span className={styles.bold}>{logDestination} &nbsp;</span>;
                } else {
                    if (is_application && !server_guid) {
                        serverSection = (
                            <span className={styles.entity}>
                                <img src={applicationIcon} alt="" />
                                {server}
                            </span>
                        );
                    } else {
                        serverSection = server_guid ? (
                            <span
                                className={styles.entity}
                                onClick={() =>
                                    this.props.handleInvestigationClick(
                                        server_guid,
                                        server,
                                        is_application
                                            ? directory_type === DIRECTORY_TYPES.aad.key
                                                ? AD_ENTITY_TYPES.APPLICATION
                                                : AD_ENTITY_TYPES.OKTA
                                            : AD_ENTITY_TYPES.SERVER
                                    )
                                }>
                                <img src={is_application ? applicationIcon : resourceIcon} alt="" />
                                {logDestination}
                            </span>
                        ) : (
                            logDestination
                        );
                    }
                }
            }

            if (this.props.entityType.id === INVESTIGATION_ENTITY_TYPES.USER.id) {
                if (this.state.data.policyType === POLICY_ACTION_TYPES.ALLOW.value) {
                    let description = '';
                    if (this.state.data.effective_rule_id) {
                        if (
                            this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE
                        ) {
                            description = ' Allow, as user is unpaired (per unpaired user policy).';
                        } else {
                            description = ' Allow, according to policy for user';
                        }
                    } else if (
                        this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE
                    ) {
                        description = ' Allow, as user is not in in an enrolled group.';
                    } else {
                        description = ' Allow (no policy).';
                    }
                    headline = (
                        <h2>
                            Accessed {serverSection} {isHost && 'from'} {hostNameSection}
                            {description}
                        </h2>
                    );
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.DENY.value) {
                    let description = '';
                    if (this.state.data.effective_rule_id) {
                        if (
                            this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE
                        ) {
                            description = ', as user is unpaired (per unpaired user policy).';
                        } else {
                            description = ', according to policy for user';
                        }
                    } else if (
                        this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE
                    ) {
                        description = ', as user is not in in an enrolled group.';
                    } else if (this.state.data.reason && DENY_REASONS[this.state.data.reason]) {
                        description = `, due to ${DENY_REASONS[this.state.data.reason]}`;
                        if (this.state.data.error_code) {
                            description += ` (${this.state.data.error_code})`;
                        }
                    }
                    headline = (
                        <h2>
                            Attempted to access {serverSection} {isHost && 'from'} {hostNameSection}{' '}
                            <span className={styles.bold}>Blocked</span>
                            {description}
                        </h2>
                    );
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.MFA.value) {
                    const [userResponse] = formatUserResponse(
                        this.state.data.user_response,
                        this.state.data.effective_rule_action
                    );
                    const userResponseDisplay =
                        userResponse !== 'n/a' ? (
                            <>
                                {' MFA Response:'} <span className={styles.bold}>{userResponse}</span>
                            </>
                        ) : null;
                    if (this.state.data.action) {
                        headline = (
                            <h2>
                                Accessed {serverSection} {isHost && 'from'} {hostNameSection} and validated by{' '}
                                <span>
                                    <span className={styles.bold}>MFA</span>
                                    {this.state.data.user_response ===
                                    AUTHENTICATION_LOG_REASONS.SAME_GROUP.USER_RESPONSE
                                        ? ' based on Policy Group.'
                                        : '.'}
                                </span>
                                {userResponseDisplay}
                            </h2>
                        );
                    } else {
                        if (this.state.data.user_response === AUTHENTICATION_LOG_REASONS.TIMEOUT) {
                            headline = (
                                <h2>
                                    Attempted to access {serverSection} {isHost && 'from'} {hostNameSection}{' '}
                                    <span className={styles.bold}>Blocked</span>, as user did not respond to an MFA
                                    request.{userResponseDisplay}
                                </h2>
                            );
                        } else {
                            headline = (
                                <h2>
                                    Attempted {null} to access {serverSection} {isHost && 'from'} {hostNameSection} and
                                    was rejected by
                                    <span>
                                        <span className={styles.bold}>MFA</span>.
                                    </span>
                                    {userResponseDisplay}
                                </h2>
                            );
                        }
                    }
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.NOTIFY.value) {
                    headline = (
                        <h2>
                            Accessed {serverSection} {isHost && 'from'} {hostNameSection} and sent notification to
                            syslog
                        </h2>
                    );
                }
            } else if (
                this.props.entityType.id === INVESTIGATION_ENTITY_TYPES.RESOURCE.id ||
                this.props.entityType.id === INVESTIGATION_ENTITY_TYPES.APPLICATION.id ||
                this.props.entityType.id === INVESTIGATION_ENTITY_TYPES.OKTA.id
            ) {
                if (this.state.data.policyType === POLICY_ACTION_TYPES.ALLOW.value) {
                    if (isDevice) {
                        headline = (
                            <h2>
                                Used {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} to access {serverSection}
                            </h2>
                        );
                    } else {
                        headline = (
                            <h2>
                                Accessed {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} {isHost && 'from'} {hostNameSection}
                            </h2>
                        );
                    }
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.DENY.value) {
                    const errorCodeText = this.state.data.error_code ? ` (${this.state.data.error_code})` : '';
                    const reasonText = (
                        <span>
                            {this.state.data.reason && DENY_REASONS[this.state.data.reason]
                                ? ` due to ${DENY_REASONS[this.state.data.reason]}${errorCodeText}`
                                : ''}
                        </span>
                    );
                    if (isDevice) {
                        headline = (
                            <h2>
                                Access attempt {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} to {serverSection} was
                                <span className={styles.bold}>Denied</span> {reasonText}
                            </h2>
                        );
                    } else {
                        headline = (
                            <h2>
                                Access attempt {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} {isHost && 'from'} {hostNameSection} was{' '}
                                <span className={styles.bold}>Denied</span> {reasonText}
                            </h2>
                        );
                    }
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.MFA.value) {
                    const [userResponse] = formatUserResponse(
                        this.state.data.user_response,
                        this.state.data.effective_rule_action
                    );
                    const userResponseDisplay =
                        userResponse !== 'n/a' ? (
                            <>
                                {' MFA Response:'} <span className={styles.bold}>{userResponse}</span>
                            </>
                        ) : null;
                    if (isDevice) {
                        if (this.state.data.action) {
                            headline = (
                                <h2>
                                    Used {usernameSection ? 'by ' : ''}
                                    {usernameSection ? usernameSection : ''} to access {serverSection} and validated by{' '}
                                    <span>
                                        <span className={styles.bold}>Multi-Factor authentication</span>
                                        {this.state.data.user_response ===
                                        AUTHENTICATION_LOG_REASONS.SAME_GROUP.USER_RESPONSE
                                            ? ' based on Policy Group.'
                                            : '.'}
                                    </span>
                                    {userResponseDisplay}
                                </h2>
                            );
                        } else {
                            headline = (
                                <h2>
                                    Access attempt {usernameSection ? 'by ' : ''}
                                    {usernameSection ? usernameSection : ''} to {serverSection} was{' '}
                                    <span className={styles.bold}>Denied</span> -
                                    <span className={styles.bold}>Multi-Factor authentication</span>
                                    <span>{' validation failed.'}</span>
                                    {userResponseDisplay}
                                </h2>
                            );
                        }
                    } else {
                        if (this.state.data.action) {
                            headline = (
                                <h2>
                                    Accessed {usernameSection ? 'by ' : ''}
                                    {usernameSection ? usernameSection : ''} {isHost && 'from'} {hostNameSection} and
                                    validated by{' '}
                                    <span>
                                        <span className={styles.bold}>Multi-Factor authentication</span>
                                        {this.state.data.user_response ===
                                        AUTHENTICATION_LOG_REASONS.SAME_GROUP.USER_RESPONSE
                                            ? ' based on Policy Group.'
                                            : '.'}
                                        {userResponseDisplay}
                                    </span>
                                </h2>
                            );
                        } else {
                            headline = (
                                <h2>
                                    Access attempt {usernameSection ? 'by ' : ''}
                                    {usernameSection ? usernameSection : ''} was{' '}
                                    <span className={styles.bold}>Denied</span> -{' '}
                                    <span className={styles.bold}>Multi-Factor authentication</span> validation failed.
                                    {userResponseDisplay}
                                </h2>
                            );
                        }
                    }
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.NOTIFY.value) {
                    if (isDevice) {
                        headline = (
                            <h2>
                                Used {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} to access {serverSection} and sent notification
                                to syslog
                            </h2>
                        );
                    } else {
                        headline = (
                            <h2>
                                Accessed {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''}
                                {isHost && 'from'} {hostNameSection} and sent notification to syslog
                            </h2>
                        );
                    }
                }
            } else if (this.props.entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id) {
                if (this.state.data.policyType === POLICY_ACTION_TYPES.ALLOW.value) {
                    let description = '';
                    if (this.state.data.effective_rule_id) {
                        if (
                            this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE
                        ) {
                            description = ' Allow, as user is unpaired (per unpaired user policy).';
                        } else {
                            description = ' Allow, according to policy for user';
                        }
                    } else if (
                        this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE
                    ) {
                        description = ' Allow, as user is not in in an enrolled group.';
                    } else {
                        description = ' Allow (no policy).';
                    }
                    headline = (
                        <h2>
                            {usernameSection} accessed {serverSection} {isHost && 'from'} {hostNameSection}
                            {description}
                        </h2>
                    );
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.DENY.value) {
                    let description = '';
                    if (this.state.data.effective_rule_id) {
                        if (
                            this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE
                        ) {
                            description = ', as user is unpaired (per unpaired user policy).';
                        } else {
                            description = ', according to policy for user';
                        }
                    } else if (
                        this.state.data.user_response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE
                    ) {
                        description = ', as user is not in in an enrolled group.';
                    } else if (this.state.data.reason && DENY_REASONS[this.state.data.reason]) {
                        description = `, due to ${DENY_REASONS[this.state.data.reason]}`;
                        if (this.state.data.error_code) {
                            description += ` (${this.state.data.error_code})`;
                        }
                    }
                    headline = (
                        <h2>
                            {usernameSection} attempted to access {serverSection} {isHost && 'from'} {hostNameSection}{' '}
                            <span className={styles.bold}>Blocked</span>
                            {description}
                        </h2>
                    );
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.MFA.value) {
                    const [userResponse] = formatUserResponse(
                        this.state.data.user_response,
                        this.state.data.effective_rule_action
                    );
                    const userResponseDisplay =
                        userResponse !== 'n/a' ? (
                            <>
                                {' MFA Response:'} <span className={styles.bold}>{userResponse}</span>
                            </>
                        ) : null;
                    if (this.state.data.action) {
                        headline = (
                            <h2>
                                {usernameSection} accessed {serverSection} {isHost && 'from'} {hostNameSection} and
                                validated by{' '}
                                <span>
                                    <span className={styles.bold}>MFA</span>
                                    {this.state.data.user_response ===
                                    AUTHENTICATION_LOG_REASONS.SAME_GROUP.USER_RESPONSE
                                        ? ' based on Policy Group.'
                                        : '.'}
                                </span>
                                {userResponseDisplay}
                            </h2>
                        );
                    } else {
                        if (this.state.data.user_response === AUTHENTICATION_LOG_REASONS.TIMEOUT) {
                            headline = (
                                <h2>
                                    {usernameSection} attempted to access {serverSection} {isHost && 'from'}{' '}
                                    {hostNameSection} <span className={styles.bold}>Blocked</span>, as user did not
                                    respond to an MFA request.
                                    {userResponseDisplay}
                                </h2>
                            );
                        } else {
                            headline = (
                                <h2>
                                    {usernameSection} attempted {null} to access {serverSection} {isHost && 'from'}{' '}
                                    {hostNameSection} and was rejected by
                                    <span>
                                        <span className={styles.bold}>MFA</span>.
                                    </span>
                                    {userResponseDisplay}
                                </h2>
                            );
                        }
                    }
                } else if (this.state.data.policyType === POLICY_ACTION_TYPES.NOTIFY.value) {
                    headline = (
                        <h2>
                            {usernameSection} accessed {serverSection} {isHost && 'from'} {hostNameSection} and sent
                            notification to syslog
                        </h2>
                    );
                }
            }
        }

        const expenderVisible =
            (this.props.entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id ||
                this.props.investigationExpenderEnabled) &&
            this.state.data.activityType !== ACTIVITY_TYPES.INCIDENT_CHANGE &&
            this.state.data.activityType !== ACTIVITY_TYPES.JIT_ACTION &&
            this.state.data.activityType !== ACTIVITY_TYPES.EVENTS_LOG &&
            this.state.data.activityType !== ACTIVITY_TYPES.RISK_CHANGE;

        return (
            <>
                <div className={styles.timelineEvent}>
                    <div className={styles.eventDate}>
                        <span className={styles.bold}>
                            {moment
                                .unix(this.props.data.time_stamp / 1000)
                                .local()
                                .format('L')}
                        </span>
                        <span>
                            {moment
                                .unix(this.props.data.time_stamp / 1000)
                                .local()
                                .format('HH:mm:ss')}
                        </span>
                    </div>
                    {this.state.data.riskChangeType === RISK_CHANGE_TYPE.LEVEL_CHANGE && (
                        <div className={`${styles.eventImage} ${styles.red}`}>
                            <i className="fa fa-exclamation"></i>
                        </div>
                    )}
                    {this.state.data.activityType === ACTIVITY_TYPES.INCIDENT_CHANGE && (
                        <div className={`${styles.eventImage} ${styles.blueWithWhiteIcon}`}>
                            <i className="fa fa-exclamation"></i>
                        </div>
                    )}
                    {this.state.data.activityType === ACTIVITY_TYPES.JIT_ACTION && (
                        <div className={`${styles.eventImage} ${styles.blueWithWhiteIcon}`}>
                            <i className="fa fa-exclamation"></i>
                        </div>
                    )}
                    {this.state.data.riskChangeType === RISK_CHANGE_TYPE.INDICATOR_CHANGE && (
                        <div className={`${styles.eventImage} ${styles.blue} ${styles.riskIndicator}`}>
                            <img src={riskIndicatorIcon} alt="" />
                        </div>
                    )}
                    {this.state.data.policyType === POLICY_ACTION_TYPES.ALLOW.value && (
                        <div className={`${styles.eventImage} ${styles.blue}`}>
                            <img src={allowEventBlueIcon} alt="" />
                        </div>
                    )}
                    {this.state.data.policyType === POLICY_ACTION_TYPES.DENY.value && (
                        <div className={`${styles.eventImage} ${styles.red}`}>
                            <img src={denyEventIcon} alt="" />
                        </div>
                    )}
                    {this.state.data.policyType === POLICY_ACTION_TYPES.MFA.value && this.state.data.action && (
                        <div className={`${styles.eventImage} ${styles.blue}`}>
                            <img src={mfaEventIcon} alt="" />
                        </div>
                    )}
                    {this.state.data.policyType === POLICY_ACTION_TYPES.MFA.value && !this.state.data.action && (
                        <div className={`${styles.eventImage} ${styles.red}`}>
                            <img src={mfaEventIcon} alt="" />
                        </div>
                    )}
                    {this.state.data.policyType === POLICY_ACTION_TYPES.NOTIFY.value && (
                        <div className={`${styles.eventImage} ${styles.green}`}>
                            <img src={alertIcon} alt="" />
                        </div>
                    )}
                    <div className={`${styles.eventContent} ${this.props.darkBackground ? styles.darkBackground : ''}`}>
                        {headline}
                    </div>
                    {this.state.data.location &&
                        this.props.entityType.id !== INVESTIGATION_ENTITY_TYPES.INCIDENT.id && (
                            <div
                                className={styles.locationImage}
                                onClick={() =>
                                    this.props.setLocation({
                                        geo_device_longitude: this.state.data.geo_device_longitude,
                                        geo_device_latitude: this.state.data.geo_device_latitude
                                    })
                                }>
                                <i className="fa fa-globe"></i>
                            </div>
                        )}
                    {expenderVisible && (
                        <div
                            className={`${styles.locationImage} ${
                                this.props.darkBackground ? styles.darkBackground : ''
                            }`}
                            onClick={() => this.setState({ isExpened: !this.state.isExpened })}>
                            {this.state.isExpened && <img src={collapseIcon} alt="collapse" />}
                            {!this.state.isExpened && <img src={expandIcon} alt="expand" />}
                        </div>
                    )}
                </div>
                <div className={`${styles.eventExtraData} ${this.props.darkBackground ? styles.darkBackground : ''}`}>
                    {this.state.data.isLoaded && this.state.isExpened && expenderVisible && (
                        <LogRowExpender
                            logData={this.state.data}
                            key={'logexpender_' + this.state.data.id}
                            rulesNames={this.props.metadataForLogs.rules}
                            wallNames={this.props.metadataForLogs.wallNames}
                            user={this.props.metadataForLogs.user.user}
                            allowDesktopMfa={this.props.metadataForLogs.allowDesktopMfa}
                            countriesList={this.props.metadataForLogs.countriesList}
                            hiddenRiskIndicators={this.props.metadataForLogs.hiddenRiskIndicators}
                            investigationView={true}
                            blackBackground={
                                this.props.entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id
                            }></LogRowExpender>
                    )}
                </div>
            </>
        );
    }
}

class InvestigationTimeline extends React.Component {
    constructor(props) {
        super(props);
        const locale = navigator.languages ? navigator.languages : navigator.language;
        moment.locale(locale);
    }
    render() {
        const timelineRows = this.props.entityActivity.map((activity, index) => {
            return (
                <TimelineRow
                    data={activity}
                    key={`timelineevent_${activity.id}_${index}`}
                    entityType={this.props.entityType}
                    entityGuid={this.props.entityGuid}
                    entityID={this.props.entityID}
                    relyingParties={this.props.relyingParties}
                    setLocation={this.props.setLocation}
                    geoLocationEnabled={this.props.geoLocationEnabled}
                    metadataForLogs={this.props.metadataForLogs}
                    darkBackground={this.props.darkBackground}
                    defaultDestinations={this.props.defaultDestinations}
                    investigationExpenderEnabled={this.props.investigationExpenderEnabled}
                    handleInvestigationClick={this.props.handleInvestigationClick}
                />
            );
        });

        return (
            <div className={`${styles.timeline} ${this.props.hideRightMargin ? styles.hideRightMargin : ''}`}>
                <div className={`${styles.eventsContainer} ${styles.lightScrollbar}`}>
                    {timelineRows.length > 0 && (
                        <InfiniteScroll
                            pageStart={0}
                            loadMore={this.props.loadActivityData}
                            hasMore={this.props.hasMoreActivity}
                            useWindow={false}
                            threshold={500}
                            initialLoad={false}
                            style={{ height: '100px' }}
                            loader={<div key={0} className={styles.activityLoader}></div>}>
                            {timelineRows}
                        </InfiniteScroll>
                    )}
                    {timelineRows.length === 0 && (
                        <div className={investigationStyles.banner}>
                            <span>No Data Found</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default InvestigationTimeline;
